/* ============================================================
   Asha Books — Bible Recommendation Quiz
   File: assets/bible-quiz.js

   Two-path quiz:
   - Q1: English vs Indian Language + price slider (hard filters)
   - English → Purpose (hard) → Preference → Translation
   - Indian  → Language (hard) → Special → Size → Binding

   Layout rules for options:
   - 2 options          → full-width column (no modifier)
   - 3–6 options        → bq__options--grid (icon-above-text card)
   - 7+ options         → bq__options--list (compact horizontal)
   - forceGrid: true    → always bq__options--grid
   ============================================================ */

(function () {
  'use strict';

  // ── Config ──────────────────────────────────────────────────────
  const CONFIG = {
    sheetId:     window.bibleQuizConfig?.sheetId     || '',
    apiKey:      window.bibleQuizConfig?.apiKey      || '',
    trackingUrl: window.bibleQuizConfig?.trackingUrl || '',
    maxResults:  3,
  };

  // ── Column maps ─────────────────────────────────────────────────
  // Sheet1 (English): Title, ISBN, Price, Handle, Purpose, Translation, Preference
  const COL = {
    title:       0,
    isbn:        1,
    price:       2,
    handle:      3,
    purpose:     4,
    translation: 5,
    preference:  6,
  };

  // Sheet2 (Indian): Title, ISBN, Price, Handle, Language, Size, Binding, Special Requirement
  const COL_INDIAN = {
    title:    0,
    isbn:     1,
    price:    2,
    handle:   3,
    language: 4,
    size:     5,
    binding:  6,
    special:  7,
  };

  // ── Questions ───────────────────────────────────────────────────

  // Q1 — Binary routing + price slider.
  const QUESTION_PATH = {
    id: 'path',
    question: 'Which language are you looking for?',
    hasPriceSlider: true,
    match: null,
    options: [
      { label: 'English',         desc: 'NIV, KJV, ESV and more',       value: 'english' },
      { label: 'Indian Language', desc: 'Telugu, Hindi, Tamil and more', value: 'indian'  },
    ],
  };

  // ── English path — Q2 onwards (Sheet1) ──────────────────────────

  const QUESTIONS_ENGLISH = [
    {
      id: 'purpose',
      question: 'What will you mainly use this Bible for?',
      match: (bible, value) => bible.purpose === value,
      // 6 options → grid layout
      options: [
        { label: 'Daily Reading',         icon: '📖', desc: 'Devotional & reading',          value: 'Daily Reading'       },
        { label: 'In depth study',        icon: '🔬', desc: 'Study Bibles with notes',        value: 'In-depth study'      },
        { label: 'Outreach / Evangelism', icon: '🎁', desc: 'Affordable, ideal for sharing',  value: 'Outreach/Evangelism' },
        { label: 'For Kids',              icon: '🧸', desc: 'Kids editions',                  value: 'Kids'                },
        { label: 'For Youth',             icon: '🎒', desc: 'Teen & youth editions',           value: 'Youth'               },
        { label: 'Carry to Church',       icon: '⛪', desc: 'Compact & portable',             value: 'Carry to Church'     },
      ],
    },
    {
      id: 'preference',
      question: 'Do any of these preferences interest you?',
      match: (bible, value) => bible.preference === value,
      // 5 options → grid layout
      options: [
        { label: 'Journaling / Artisan', icon: '✍️', desc: 'Wide margins for notes',        value: 'Journaling'  },
        { label: 'Large Print only',     icon: '🔍', desc: 'Bigger, easier to read',         value: 'Large Print' },
        { label: 'Compact size',         icon: '🗜️', desc: 'Small & portable',               value: 'Compact'     },
        { label: 'Thinline',             icon: '📏', desc: 'Slim, easy to carry to church',  value: 'Thinline'    },
        { label: 'No preference',        icon: '🤷',                                         value: null          },
      ],
    },
    {
      id: 'translation',
      question: 'Which translation do you prefer?',
      subtitle: 'Not sure? Choose "No preference" to see all translations.',
      forceTwoCol: true,
      match: (bible, value) => bible.translation === value,
      // 15 options → list layout
      options: [
        { label: 'NIV',           icon: '📘', desc: 'Easy to read',         value: 'NIV'       },
        { label: 'KJV',           icon: '📜', desc: 'Classic English',       value: 'KJV'       },
        { label: 'ESV',           icon: '📗', desc: 'Accurate & readable',   value: 'ESV'       },
        { label: 'NKJV',          icon: '📙', desc: 'Modern classic',        value: 'NKJV'      },
        { label: 'NLT',           icon: '📕', desc: 'Contemporary',          value: 'NLT'       },
        { label: 'NIrV',          icon: '🌟', desc: 'Easy reader',           value: 'NIrV'      },
        { label: 'GNT',           icon: '🌍', desc: 'Good News Translation', value: 'GNT'       },
        { label: 'NASB',          icon: '⚖️', desc: 'Very literal',          value: 'NASB'      },
        { label: 'NRSV',          icon: '🎓', desc: 'Scholarly',             value: 'NRSV'      },
        { label: 'The Message',   icon: '💬', desc: 'Narrative paraphrase',  value: 'Message'   },
        { label: 'Amplified',     icon: '🔊', desc: 'Expanded meaning',      value: 'Amplified' },
        { label: 'NET',           icon: '🖊️', desc: 'Scholar notes inline',  value: 'NET'       },
        { label: 'Compass',       icon: '🧭', desc: 'Voice translation',     value: 'Compass'   },
        { label: 'NIC',           icon: '📖', desc: '',                      value: 'NIC'       },
        { label: 'No preference', icon: '🤷', desc: 'Show me everything',    value: null        },
      ],
    },
  ];

  // ── Indian language path — Q2 onwards (Sheet2) ──────────────────

  const QUESTIONS_INDIAN = [
    {
      id: 'language',
      question: 'Which language?',
      match: (bible, value) => bible.language === value,
      forceGrid: true,
      options: [
        { label: 'Telugu',        icon: 'తె',  isScript: true, value: 'Telugu'    },
        { label: 'Hindi',         icon: 'हि',  isScript: true, value: 'Hindi'     },
        { label: 'Tamil',         icon: 'த',   isScript: true, value: 'Tamil'     },
        { label: 'Malayalam',     icon: 'മ',   isScript: true, value: 'Malayalam' },
        { label: 'Bengali',       icon: 'বাং', isScript: true, value: 'Bengali'   },
        { label: 'Nepali',        icon: 'ने',  isScript: true, value: 'Nepali'    },
        { label: 'Kannada',       icon: 'ಕ',   isScript: true, value: 'Kannada'   },
        { label: 'Punjabi',       icon: 'ਪੰ',  isScript: true, value: 'Punjabi'   },
        { label: 'No preference', icon: '🤷',                  value: null        },
      ],
    },
    {
      id: 'special',
      question: "What's it mainly for?",
      match: (bible, value) => {
        if (value === 'standard') return !bible.special;
        return bible.special === value;
      },
      // 6 options → grid layout
      options: [
        { label: 'Daily reading',        icon: '📖', desc: 'Standard edition',    value: 'standard'       },
        { label: 'Study',                icon: '🔬', desc: 'Study Bible',          value: 'Study'          },
        { label: 'For a child',          icon: '🧸', desc: 'Kids edition',         value: 'Kids'           },
        { label: 'Senior / Large print', icon: '👓', desc: 'Easier to read',       value: 'Senior Citizen' },
        { label: 'Bilingual',            icon: '🔀', desc: 'Two languages in one', value: 'Bilingual'      },
        { label: 'No preference',        icon: '🤷',                               value: null             },
      ],
    },
    {
      id: 'size',
      question: 'What size do you prefer?',
      match: (bible, value) => {
        if (value === 'compact') return ['Compact', 'Semi-compact'].includes(bible.size);
        return bible.size === value;
      },
      // 5 options → grid layout
      options: [
        { label: 'Pocket',        icon: '🤏', desc: 'Small, travel-friendly', value: 'Pocket'  },
        { label: 'Compact',       icon: '📐', desc: 'Slightly larger',         value: 'compact' },
        { label: 'Regular',       icon: '📖', desc: 'Standard size',           value: 'Regular' },
        { label: 'Large',         icon: '📚', desc: 'Bigger text & pages',     value: 'Large'   },
        { label: 'No preference', icon: '🤷',                                  value: null      },
      ],
    },
    {
      id: 'binding',
      question: 'What type of cover?',
      match: (bible, value) => {
        if (value === 'affordable') return ['Vinyl', 'Plastic Comb', 'Paperback'].includes(bible.binding);
        if (value === 'premium')    return ['PU Leather-like', 'Bonded Leather', 'Amity'].includes(bible.binding);
        if (value === 'hardcover')  return bible.binding === 'Hardcover';
        return true;
      },
      // 4 options → grid layout
      options: [
        { label: 'Affordable',    icon: '💰', desc: 'Vinyl or plastic cover', value: 'affordable' },
        { label: 'Premium',       icon: '✨', desc: 'Leather-look or bonded', value: 'premium'    },
        { label: 'Hardcover',     icon: '📕', desc: 'Classic rigid cover',    value: 'hardcover'  },
        { label: 'No preference', icon: '🤷',                                 value: null         },
      ],
    },
  ];

  // ── Filter fallback orders ───────────────────────────────────────
  const FILTER_PRIORITY_ENGLISH = [
    ['purpose', 'preference', 'translation'],
    ['purpose', 'preference'],
    ['purpose', 'translation'],
    ['purpose'],
  ];

  const FILTER_PRIORITY_INDIAN = [
    ['language', 'special', 'size', 'binding'],
    ['language', 'special', 'size'],
    ['language', 'special'],
    ['language'],
  ];

  const FALLBACK_MSG = 'Sorry, we could not find a Bible that perfectly matches your requirements. Here are some other options that may interest you!';

  // ── Price Presets ────────────────────────────────────────────────
  const PRICE_PRESETS = [
    { label: 'Any price',       min: 0,    max: Infinity },
    { label: 'Under ₹500',      min: 0,    max: 500      },
    { label: '₹500 – ₹1,000',   min: 500,  max: 1000     },
    { label: '₹1,000 – ₹2,000', min: 1000, max: 2000     },
  ];

  // ── State ────────────────────────────────────────────────────────
  let answers     = [];
  let catalog     = [];
  let pricePreset = PRICE_PRESETS[0]; // default: any price

  // ── Path detection ───────────────────────────────────────────────
  function isEnglishPath() {
    return !answers[0] || answers[0].value === 'english';
  }

  function getActiveQuestions() {
    return [QUESTION_PATH, ...(isEnglishPath() ? QUESTIONS_ENGLISH : QUESTIONS_INDIAN)];
  }

  // ── Helpers ──────────────────────────────────────────────────────
  function buildAnswerMap() {
    const map = {};
    getActiveQuestions().forEach((q, i) => {
      if (answers[i] !== undefined) map[q.id] = answers[i].value;
    });
    return map;
  }

  function parsePrice(str) {
    return parseInt(String(str || '').replace(/[₹,\s]/g, '')) || 0;
  }

  // ── Layout class for a question ──────────────────────────────────
  function getLayoutClass(q) {
    if (q.forceTwoCol) return 'bq__options--two-col';
    if (q.forceGrid || (q.options.length >= 3 && q.options.length <= 6)) {
      return 'bq__options--grid';
    }
    if (q.options.length > 6) {
      return 'bq__options--list';
    }
    return '';
  }

  // ── Init ─────────────────────────────────────────────────────────
  function init() {
    const container = document.getElementById('bible-quiz');
    if (!container) return;
    renderShell(container);
    showStep(0);
  }

  // ── Shell ────────────────────────────────────────────────────────
  function renderShell(container) {
    container.innerHTML = `
      <div class="bq">
        <div class="bq__progress">
          <div class="bq__progress-bar" id="bq-progress"></div>
        </div>
        <div id="bq-question-area"></div>
        <div class="bq__loading" id="bq-loading">
          <div class="bq__wave">
            <span></span><span></span><span></span><span></span><span></span>
            <span></span><span></span><span></span><span></span>
          </div>
          <p>Analysing your preferences…</p>
        </div>
        <div class="bq__results" id="bq-results">
          <h2 class="bq__results-title">Your recommended Bibles</h2>
          <p class="bq__results-sub">Based on your answers, here are the best matches from our collection.</p>
          <div class="bq__fallback-notice hidden" id="bq-fallback-notice"></div>
          <div class="bq__products" id="bq-products"></div>
          <div class="bq__results-footer">
            <button class="bq__restart" id="bq-restart" type="button">← Start over</button>
            <a href="/collections/all-products" class="bq__browse-all">Browse all Bibles →</a>
          </div>
        </div>
      </div>
    `;
    document.getElementById('bq-restart').addEventListener('click', restart);
  }

  // ── Render a Step ────────────────────────────────────────────────
  function showStep(stepIndex) {
    const questions   = getActiveQuestions();
    const q           = questions[stepIndex];
    const isLastStep  = stepIndex === questions.length - 1;
    const savedAnswer = answers[stepIndex];
    const layoutClass = getLayoutClass(q);
    const progressPct = (stepIndex / questions.length) * 100;

    document.getElementById('bq-progress').style.width = progressPct + '%';

    const sliderHtml = q.hasPriceSlider ? `
      <div class="bq__price-section">
        <div class="bq__price-title">Budget</div>
        <div class="bq__price-presets">
          ${PRICE_PRESETS.map((p, i) => `
            <button class="bq__price-preset${pricePreset === PRICE_PRESETS[i] ? ' is-selected' : ''}"
                    data-preset="${i}" type="button">${p.label}</button>
          `).join('')}
        </div>
      </div>
    ` : '';

    const area = document.getElementById('bq-question-area');
    area.innerHTML = `
      <div class="bq__step${q.hasPriceSlider ? ' bq__step--has-slider' : ''}">
        <div class="bq__counter">Question ${stepIndex + 1} of ${questions.length}</div>
        <h2 class="bq__question">${q.question}</h2>
        ${q.subtitle ? `<p class="bq__subtitle">${q.subtitle}</p>` : ''}
        <div class="bq__options ${layoutClass}">
          ${q.options.map((opt, i) => `
            <button class="bq__option ${savedAnswer?.optionIndex === i ? 'is-selected' : ''}"
                    data-index="${i}" type="button">
              ${opt.icon ? `<span class="bq__opt-icon${opt.isScript ? ' bq__opt-icon--script' : ''}">${opt.icon}</span>` : ''}
              <span class="bq__opt-text">
                ${opt.label}
                ${opt.desc ? `<span class="bq__opt-desc">${opt.desc}</span>` : ''}
              </span>
            </button>
          `).join('')}
        </div>
        ${sliderHtml}
        <div class="bq__nav">
          ${stepIndex > 0
            ? `<button class="bq__btn bq__btn--back" id="bq-back" type="button">← Back</button>`
            : '<span></span>'}
          <button class="bq__btn bq__btn--next" id="bq-next" type="button"
                  ${savedAnswer === undefined ? 'disabled' : ''}>
            ${isLastStep ? 'Find my Bible →' : 'Next →'}
          </button>
        </div>
      </div>
    `;

    let selectedIndex = savedAnswer?.optionIndex ?? null;

    area.querySelectorAll('.bq__option').forEach((btn, i) => {
      btn.addEventListener('click', () => {
        area.querySelectorAll('.bq__option').forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        selectedIndex = i;
        document.getElementById('bq-next').disabled = false;
      });
    });

    // Price preset listeners (Q1 only)
    if (q.hasPriceSlider) {
      area.querySelectorAll('.bq__price-preset').forEach(btn => {
        btn.addEventListener('click', () => {
          area.querySelectorAll('.bq__price-preset').forEach(b => b.classList.remove('is-selected'));
          btn.classList.add('is-selected');
          pricePreset = PRICE_PRESETS[parseInt(btn.dataset.preset)];
        });
      });
    }

    document.getElementById('bq-next').addEventListener('click', () => {
      if (selectedIndex === null) return;
      const opt = q.options[selectedIndex];
      if (stepIndex === 0) answers = [];
      answers[stepIndex] = { value: opt.value, optionIndex: selectedIndex };
      isLastStep ? fetchAndShow() : showStep(stepIndex + 1);
    });

    const backBtn = document.getElementById('bq-back');
    if (backBtn) {
      backBtn.addEventListener('click', () => showStep(stepIndex - 1));
    }
  }

  // ── Google Sheets Fetch ──────────────────────────────────────────
  async function fetchCatalog() {
    const english = isEnglishPath();
    const range   = english ? 'Sheet1!A:G' : 'Sheet2!A:H';
    const url     = `https://sheets.googleapis.com/v4/spreadsheets/${CONFIG.sheetId}/values/${range}?key=${CONFIG.apiKey}`;
    const res     = await fetch(url);
    if (!res.ok) throw new Error(`Sheets API error: ${res.status}`);
    const data = await res.json();
    const rows = (data.values || []).slice(1);

    if (english) {
      return rows
        .map(row => ({
          title:       row[COL.title]       || '',
          price:       parsePrice(row[COL.price]),
          handle:      row[COL.handle]      || '',
          purpose:     row[COL.purpose]     || '',
          translation: row[COL.translation] || '',
          preference:  row[COL.preference]  || '',
        }))
        .filter(b => b.handle);
    } else {
      return rows
        .map(row => ({
          title:    row[COL_INDIAN.title]    || '',
          price:    parsePrice(row[COL_INDIAN.price]),
          handle:   row[COL_INDIAN.handle]   || '',
          language: row[COL_INDIAN.language] || '',
          size:     row[COL_INDIAN.size]     || '',
          binding:  row[COL_INDIAN.binding]  || '',
          special:  row[COL_INDIAN.special]  || '',
        }))
        .filter(b => b.handle);
    }
  }

  // ── Matching ─────────────────────────────────────────────────────
  function filterCatalog(activeQuestionIds) {
    const answerMap = buildAnswerMap();
    const questions = getActiveQuestions();
    return catalog.filter(bible => {
      // Price hard filter (always applied)
      if (bible.price < pricePreset.min || bible.price > pricePreset.max) return false;

      return questions
        .filter(q => q.match && activeQuestionIds.includes(q.id))
        .every(q => {
          const value = answerMap[q.id];
          if (value === null || value === undefined) return true;
          return q.match(bible, value);
        });
    });
  }

  function findMatches() {
    const priority  = isEnglishPath() ? FILTER_PRIORITY_ENGLISH : FILTER_PRIORITY_INDIAN;
    const answerMap = buildAnswerMap();

    // Soft-filter keys are Q3/Q4 — only show the fallback message when the user
    // actually picked one of these AND we had to drop it to find results.
    const softKeys   = isEnglishPath() ? ['preference', 'translation'] : ['size', 'binding'];
    const pickedSoft = softKeys.filter(k => answerMap[k] != null);

    for (let i = 0; i < priority.length; i++) {
      const results = filterCatalog(priority[i]);
      if (results.length >= 1) {
        const droppedSoft = pickedSoft.some(k => !priority[i].includes(k));
        return { results, usedFallback: droppedSoft };
      }
    }
    // Nothing matched at all — return full catalog without fallback message
    return { results: catalog.slice(0, CONFIG.maxResults), usedFallback: false };
  }

  // ── Response Tracking ────────────────────────────────────────────
  function trackResponse(results, usedFallback) {
    if (!CONFIG.trackingUrl) return;
    const answerMap = buildAnswerMap();
    const questions = getActiveQuestions();
    const payload = {
      timestamp:    Date.now(),
      path:         answerMap.path                   || '',
      budget:       pricePreset.label,
      q2:           answerMap[questions[1]?.id]      ?? '',
      q3:           answerMap[questions[2]?.id]      ?? '',
      q4:           answerMap[questions[3]?.id]      ?? '',
      q5:           answerMap[questions[4]?.id]      ?? '',
      results:      results.map(b => b.handle),
      usedFallback,
    };
    // fire-and-forget — GET avoids the Apps Script redirect/CORS issue
    const url = CONFIG.trackingUrl + '?data=' + encodeURIComponent(JSON.stringify(payload));
    fetch(url, { mode: 'no-cors' }).catch(() => {});
  }

  // ── Fetch Sheet + Show Results ───────────────────────────────────
  async function fetchAndShow() {
    document.getElementById('bq-question-area').style.display = 'none';
    document.getElementById('bq-loading').style.display       = 'flex';
    document.getElementById('bq-progress').style.width        = '100%';

    try {
      catalog = await fetchCatalog();
      const { results: matched, usedFallback } = findMatches();
      const top            = matched.slice(0, CONFIG.maxResults);
      trackResponse(top, usedFallback);
      const uniqueHandles  = [...new Set(top.map(b => b.handle))];
      const imageMap       = await loadImages(uniqueHandles);
      renderResults(top, imageMap, usedFallback);
    } catch (err) {
      console.error('[Bible Quiz] Failed to load catalog:', err);
      showError();
    }
  }

  async function loadImages(handles) {
    const imageMap = {};
    await Promise.all(
      handles.map(async (handle) => {
        try {
          const res = await fetch(`/products/${handle}.json`);
          const { product } = await res.json();
          imageMap[handle] = product?.images?.[0]?.src || null;
        } catch {
          imageMap[handle] = null;
        }
      })
    );
    return imageMap;
  }

  // ── Render Results ───────────────────────────────────────────────
  function renderResults(bibles, imageMap, usedFallback) {
    document.getElementById('bq-loading').style.display = 'none';
    document.getElementById('bq-results').style.display = 'block';

    // Fallback notice
    const noticeEl = document.getElementById('bq-fallback-notice');
    if (usedFallback && bibles.length > 0) {
      noticeEl.textContent = FALLBACK_MSG;
      noticeEl.classList.remove('hidden');
    } else {
      noticeEl.classList.add('hidden');
    }

    const productsEl = document.getElementById('bq-products');

    if (bibles.length === 0) {
      productsEl.innerHTML = `
        <div class="bq__no-results">
          <p>We couldn't find an exact match — but we have plenty of Bibles to explore.</p>
          <a href="/collections/bibles" class="bq__btn bq__btn--next"
             style="display:inline-block;margin-top:16px;text-decoration:none;">
            Browse all Bibles →
          </a>
        </div>
      `;
      return;
    }

    productsEl.innerHTML = bibles.map(b => {
      const imgSrc = imageMap[b.handle];
      return `
        <div class="bq__product-card">
          ${imgSrc
            ? `<img class="bq__product-img" src="${imgSrc}" alt="${b.title}" loading="lazy">`
            : `<div class="bq__product-img bq__product-img--placeholder"></div>`}
          <div class="bq__product-info">
            <div class="bq__product-title">${b.title}</div>
            <div class="bq__product-price">&#8377;${b.price.toLocaleString('en-IN')}</div>
            <a class="bq__product-btn" href="/products/${b.handle}">View Bible</a>
          </div>
        </div>
      `;
    }).join('');
  }

  function showError() {
    document.getElementById('bq-loading').style.display = 'none';
    document.getElementById('bq-results').style.display = 'block';
    document.getElementById('bq-products').innerHTML = `
      <div class="bq__no-results">
        <p>Something went wrong loading our catalog. Please try refreshing the page.</p>
      </div>
    `;
  }

  // ── Restart ──────────────────────────────────────────────────────
  function restart() {
    answers     = [];
    catalog     = [];
    pricePreset = PRICE_PRESETS[0];
    document.getElementById('bq-results').style.display       = 'none';
    document.getElementById('bq-question-area').style.display = 'flex';
    showStep(0);
  }

  // ── Boot ─────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
